import { API, triggerDownload } from '../../../../service/common/api';
import { FIELD_URL } from '../../../../service/common/env';
import { treatFieldData, treatFieldPaddockData } from './field-helper';
import { EnumProcessingStatus } from '../../../../pages/map/service/common/types';
import { Feature } from '@turf/helpers';
import { showToast } from '../../../../pages/map/service/common/utils';
import { isArray } from 'lodash';
import { Journey } from '../../../../pages/customer-profile/types/GrowerService';
import { EnrollmentStatus } from '../../../../pages/map/types/Field';
import * as turf from '@turf/turf';
import { AddressSearchService } from '../../../../pages/map/service/AddressSearchService';
import { ValidationResult } from '../../../reducers/map-reducer';
import { getTrainingRegions } from '../map-action';

export const getFields: any = (journeyId: string, startDate?: Date) => async (dispatch: any) => {
  dispatch({ type: 'GET_FIELDS_REQUESTED' });

  const limit = 70;

  let finalFields: any[] = [];
  let initialLength = 0,
    finalLength = limit;
  let result;

  try {
    while (finalLength - initialLength === limit) {
      initialLength = finalFields.length;

      result = await API.get(`${FIELD_URL}/field-service/fields`, {
        journeyId: journeyId,
        getPaddocks: 'true',
        timestamp: startDate,
        limit,
        offset: finalFields.length,
      });

      finalFields = finalFields.concat(result.data);
      finalLength = finalFields.length;
    }

    const paddocks: { [key: string]: Feature[] } = {};
    const mappedFields = finalFields.map((field) => {
      paddocks[field.id] = [];

      if (field.layout?.paddocks?.length) {
        paddocks[field.id] = treatFieldPaddockData(field);
      }
      return treatFieldData(field);
    });

    dispatch({
      type: 'GET_FIELDS_SUCCEEDED',
      payload: {
        fields: mappedFields,
        paddocks,
      },
    });
  } catch (e) {
    dispatch({
      type: 'GET_FIELDS_ERRORED',
      payload: e,
    });
    throw e;
  }
};

export const getShapefileStatus: any = (journeyId: string) => (dispatch: any) => {
  dispatch({ type: 'GET_SHAPEFILES_REQUESTED' });
  return API.get(`${FIELD_URL}/field-service/shapefiles/status?journeyId=${journeyId}`)
    .then((res) => {
      dispatch({
        type: 'GET_SHAPEFILES_SUCCEEDED',
        payload: res.data?.shapefileStatus,
      });
    })
    .catch((e) => {
      dispatch({
        type: 'GET_SHAPEFILES_ERRORED',
        payload: e,
      });
    });
};

const mapFieldsByCounty = (fields: Feature[]) =>
  fields.reduce((acc, field) => {
    const county = field.properties?.county || 'unknown';
    if (!acc[county]) acc[county] = [];
    acc[county].push(field);
    return acc;
  }, {} as Record<string, Feature[]>);

const sequentialBatchRequest = async (records: Feature[][], batchSize: number, url: string) => {
  const results: any[] = [];

  for (const fields of records) {
    for (let i = 0; i < fields.length; i += batchSize) {
      const data = fields.slice(i, i + batchSize).map((f) => ({
        boundaries: f.properties?.processedProperties?.original.boundaries ?? f,
        unenrolledAt: f.properties?.unenrolledAt,
      }));

      const res = await API.post(url, data);
      if (res?.data?.length) results.push(...res.data);
    }
  }

  return results;
};

export const verifyOverlap: any = (journeyId: string, fields: Feature[]) => (dispatch: any, getState: any) => {
  const { map } = getState();
  if (map.loadingOverlap) return;

  dispatch({ type: 'VERIFY_OVERLAP_REQUESTED' });

  const BATCH_SIZE = 50;
  const url = `${FIELD_URL}/field-service/fields/intersections?journeyId=${journeyId}`;
  const countyFields = Object.values(mapFieldsByCounty(fields));

  sequentialBatchRequest(countyFields, BATCH_SIZE, url)
    .then((data) => dispatch({ type: 'VERIFY_OVERLAP_SUCCEEDED', payload: data }))
    .catch((err) => dispatch({ type: 'VERIFY_OVERLAP_ERRORED', payload: err }));
};

export const verifyNearFields: any = (journeyId: string, fields: Feature[], t: (key: string) => string) => (dispatch: any, getState: any) => {
  const { map } = getState();
  if (map.loadingNearFields) return;

  dispatch({ type: 'VERIFY_NEAR_FIELDS_REQUESTED' });

  const BATCH_SIZE = 50;
  const url = `${FIELD_URL}/field-service/fields/near-fields?journeyId=${journeyId}`;
  const countyFields = Object.values(mapFieldsByCounty(fields));

  sequentialBatchRequest(countyFields, BATCH_SIZE, url)
    .then((data) => dispatch({ type: 'VERIFY_NEAR_FIELDS_SUCCEEDED', payload: data }))
    .catch((err) => {
      dispatch({ type: 'VERIFY_NEAR_FIELDS_ERRORED', payload: err });
      showToast(t('fieldFlow.errors.error'), `${t('fieldFlow.errors.nearFields')}`, 'error', 'near-fields-error');
    });
};

export const addFieldAction: any = (field?: Feature | Feature[]) => async (dispatch: any) => {
  if (field) {
    const fields = isArray(field) ? field : [field];

    dispatch({
      type: 'ADD_FIELD',
      payload: fields,
    });
    return;
  }

  dispatch({
    type: 'ADD_FIELD',
  });
};

export const getLastFieldNum: any = (journeyId: string) => (dispatch: any) => {
  dispatch({
    type: 'LAST_FIELD_NUM_REQUESTED',
  });
  API.get(`${FIELD_URL}/field-service/fields/field-number`, { journeyId })
    .then((res) => {
      dispatch({
        type: 'LAST_FIELD_NUM_SUCCEEDED',
        payload: res.data + 1,
      });
    })
    .catch((e) => {
      dispatch({
        type: 'LAST_FIELD_NUM_ERRORED',
        payload: e,
      });
    });
};

export const setTotalFieldsArea: any = (fields: Feature[]) => (dispatch: any) => {
  const enrolledFields = fields.filter((f: Feature) => !f.properties?.unenrolledAt);

  const areaAcre = enrolledFields.reduce((totalAcres: number, f) => totalAcres + Number(f.properties?.area ?? 0), 0).toFixed(2);
  const areaHectare = enrolledFields.reduce((totalAcres: number, f) => totalAcres + Number(f.properties?.areaHectare ?? 0), 0).toFixed(2);

  dispatch({
    type: 'SET_TOTAL_FIELDS_AREA',
    payload: {
      areaAcre,
      areaHectare,
    },
  });
};

export const startSplittingField: any = (field: string | Feature) => (dispatch: any) => {
  dispatch({
    type: 'UPDATE_FIELD',
    payload: {
      editMode: 'SPLIT',
      field,
    },
  });
};

export const startEditingField: any = (field: string | Feature) => (dispatch: any) => {
  dispatch({
    type: 'UPDATE_FIELD',
    payload: {
      editMode: 'EDIT',
      field,
    },
  });
};

export const getFieldsValidations: any = (fieldsToCheck: Feature[], allFields: Feature[]) => async (dispatch: any) => {
  dispatch({
    type: 'FIELDS_VALIDATION',
  });

  const BATCH_SIZE = 30;
  const simultaneousRequests = 4;
  let promises: Promise<any>[] = [];

  const results: ValidationResult = {
    selfIntersections: [],
    intersectionsBetweenFields: [],
  };

  for (let i = 0; i < fieldsToCheck.length; i += BATCH_SIZE) {
    const batch = fieldsToCheck.slice(i, i + BATCH_SIZE);

    promises.push(
      API.post(`${FIELD_URL}/field-service/fields/validate-fields`, { selfOnly: true, fields: batch }).then((res) => {
        results.selfIntersections.push(...res.data.selfIntersections);
      }),
    );

    if (promises.length >= simultaneousRequests) {
      await Promise.all(promises);
      promises = [];
    }
  }

  const others = [...allFields];
  for (let i = 0; i < fieldsToCheck.length; i += BATCH_SIZE) {
    for (let j = 0; j < others.length; j += BATCH_SIZE) {
      const otherBatch = others.slice(j, j + BATCH_SIZE);

      promises.push(
        API.post(`${FIELD_URL}/field-service/fields/validate-fields`, {
          selfOnly: false,
          fields: fieldsToCheck.slice(i, i + BATCH_SIZE),
          others: otherBatch,
        }).then((res) => {
          if (res.data.intersectionsBetweenFields) {
            results.intersectionsBetweenFields.push(...res.data.intersectionsBetweenFields);
          }
        }),
      );

      if (promises.length >= simultaneousRequests) {
        await Promise.all(promises);
        promises = [];
      }
    }
    others.splice(others.indexOf(fieldsToCheck[i]), BATCH_SIZE);
  }

  await Promise.all(promises);

  dispatch({
    type: 'FIELDS_VALIDATION_SUCCEEDED',
    payload: {
      intersections: results,
      fieldsValidated: fieldsToCheck,
    },
  });
};

export const updateField: any = () => async (dispatch: any) => {
  dispatch({
    type: 'UPDATE_FIELD_BOUNDARY',
  });
};

export const updateFieldInfo: any = (field: Feature) => (dispatch: any) => {
  dispatch({
    type: 'UPDATE_FIELD_INFO',
    payload: {
      field,
    },
  });
};

export const removeFields: any = (deletedField?: Feature) => async (dispatch: any) => {
  if (!deletedField) {
    dispatch({
      type: 'REMOVE_ALL_FIELDS',
    });
    return;
  }

  dispatch({
    type: 'REMOVE_FIELD',
    payload: deletedField,
  });
};

const deleteFields = (removedFields: Feature[]) => (dispatch: any) => {
  Promise.all(removedFields.map((f) => API.delete(`${FIELD_URL}/field-service/fields/${f.id}`))).catch(() => null);
  dispatch({
    type: 'DELETE_FIELDS',
  });
};

export const saveFields: any =
  (
    journey: Journey,
    fields: Feature[],
    removedFields: Feature[],
    updatedFieldBoundaries: boolean,
    navigateBack?: () => Promise<void>,
    saveShapefile?: () => Promise<void>,
  ) =>
  (dispatch: any) => {
    dispatch(deleteFields(removedFields));
    if (!updatedFieldBoundaries) {
      if (navigateBack) return navigateBack();
      else return Promise.resolve();
    }

    const body = fields.map((fieldItem) => {
      const field = structuredClone(fieldItem);
      field.properties = field.properties || {};
      delete field.properties.selected;
      delete field.properties.lineColor;
      delete field.properties.lineWidth;
      delete field.properties.fillColor;
      delete field.properties.fillOpacity;
      return {
        id: field.id,
        growerId: journey?.leadId ?? journey?.opportunityId,
        opportunityId: journey?.opportunityId,
        journeyId: journey?.id,
        name: field.properties.name,
        area: field.properties.area,
        areaHectare: field.properties.areaHectare,
        status: 'VISIBLE',
        situation: field.properties.situation,
        leaseAgreementType: field.properties.leaseAgreementType,
        leaseStartDate: field.properties.leaseStartDate,
        leaseEndDate: field.properties.leaseEndDate,
        practiceType: field.properties.practiceType,
        dominantPlants: field.properties.dominantPlants,
        practices: field.properties.practices,
        paymentOption: field.properties.paymentOption,
        processedIneligibleBoundaries: field.properties.processedIneligibleBoundaries,
        boundaries: {
          ...field,
          properties: {
            ineligibleAreaAcres: field.properties.ineligibleAreaAcres,
            ineligibleAreaHectares: field.properties.ineligibleAreaHectares,
            ineligibleAreasFinalized: field.properties.ineligibleAreasFinalized,
            area: field.properties.area,
            areaHectare: field.properties.areaHectare,
            intersects: field.properties.intersects,
            overlaps: field.properties.overlaps,
          },
        },
        country: field.properties.country,
        county: field.properties.county,
        state: field.properties.state,
        irrigated: field.properties.irrigated,
        legume: field.properties.legume,
        previousTillage: field.properties.previousTillage,
      };
    });

    dispatch({
      type: 'SAVE_FIELDS_REQUESTED',
      payload: body,
    });
    const promises: Promise<any>[] = [API.post(`${FIELD_URL}/field-service/fields`, body)];

    if (saveShapefile) {
      promises.push(saveShapefile());
    }

    return Promise.all(promises)
      .then(() => {
        dispatch({
          type: 'SAVE_FIELDS_SUCCEEDED',
        });
        if (navigateBack) return navigateBack();
      })
      .catch((err) => {
        console.log(`Couldn't save fields: saveFields error - ${JSON.stringify(err)}`);
        dispatch({
          type: 'SAVE_FIELDS_ERRORED',
          payload: err,
        });
        throw err;
      });
  };

export const focusField: any = (fieldId?: string) => (dispatch: any) => {
  dispatch({
    type: 'FOCUS_FIELD',
    payload: fieldId,
  });
};

export const hideFields: any = (hide: boolean) => (dispatch: any) => {
  dispatch({
    type: 'HIDE_FIELDS',
    payload: hide,
  });
};

export const updatePaddock: any = (feature: Feature) => (dispatch: any) => {
  dispatch({
    type: 'UPDATE_PADDOCK',
    payload: feature,
  });
};

export const restartPaddock: any = () => (dispatch: any) => {
  dispatch({
    type: 'RESTART_PADDOCK',
  });
};

export const setFinishedDrawingFields: any = () => (dispatch: any) => {
  dispatch({
    type: 'SET_FINISHED_FIELDS',
  });
};

export const setDrawnPaddocksForField: any = (field: Feature) => (dispatch: any) => {
  dispatch({
    type: 'SET_DRAWN_PADDOCKS_FOR_FIELD',
    payload: field,
  });
};

export const getSyncAvailability: any = (country: string, upsellingFlow: boolean) => (dispatch: any) => {
  dispatch({
    type: 'GET_SYNC_AVAILABILITY',
    payload: {
      country,
      upsellingFlow,
    },
  });
};

export const changeFieldEnrollmentStatus: any = (field: Feature, status: boolean, hidden: boolean) => (dispatch: any) => {
  dispatch({
    type: 'LOADING_FIELDS',
  });
  const URL = `${FIELD_URL}/field-service/fields/enrolled-status/${field.id}`;
  const body = { status: !status ? EnrollmentStatus.ENROLLED : EnrollmentStatus.UNENROLLED };
  API.post(URL, body).then(() => {
    dispatch({
      type: 'CHANGE_ENROLLMENT',
      payload: {
        field,
        status,
        hidden,
      },
    });
  });
};

export const processIneligibleAreas: any = (journeyId: string, onProcessed: () => void) => (dispatch: any) => {
  API.post(`${FIELD_URL}/field-service/fields/field-processing/${journeyId}`, {}).then(() => {
    dispatch({
      type: 'SET_PROCESSING_STATUS',
      payload: EnumProcessingStatus.PROCESSING,
    });

    const interval: any = setInterval(async () => {
      try {
        const { data } = await API.get(`${FIELD_URL}/field-service/fields/field-processing/status/${journeyId}`);
        if (data.status === EnumProcessingStatus.PROCESSED) {
          clearInterval(interval);
          onProcessed();
        }
      } catch (e) {
        // TODO: find a better way to handle exceptions
        console.log(`getFieldProcessingStatus error - `, JSON.stringify(e));
      }
    }, 5000);
  });
};

export const finalizeFieldProcessing: any = (journeyId: string, choices: string[], onProcessed: () => void) => (dispatch: any) => {
  API.post(`${FIELD_URL}/field-service/fields/field-processing/${journeyId}/finalize-ineligible-area`, {
    choices,
  }).then((res) => {
    dispatch({
      type: 'SET_INELIGIBLE_AREAS',
      payload: res.data?.status === 'FINALIZED',
    });
    onProcessed();
  });
};

export const setUploadingDrawnField: any = (show: boolean) => (dispatch: any) => {
  dispatch({
    type: 'SET_UPLOADING_DRAWN_FIELD',
    payload: show,
  });
};

export const setUploadingBoundaries: any = (uploading: boolean) => (dispatch: any) => {
  dispatch({
    type: 'SET_UPLOADING_BOUNDARIES',
    payload: uploading,
  });
};

export const checkFieldLocation: any = (field: Feature) => (dispatch: any) => {
  dispatch({
    type: 'CHECK_FIELD_LOCATION',
    payload: field.id,
  });
  const fieldCenter = turf.center(turf.featureCollection([field])).geometry.coordinates;
  AddressSearchService.searchStateAndCounty(fieldCenter.join(','), ['district', 'region']).then((res) => {
    dispatch({
      type: 'CHECK_FIELD_LOCATION_SUCCEEDED',
      payload: {
        ...field,
        properties: {
          ...field.properties,
          ...res,
        },
      },
    });
  });
};

export const setClassificationLayer: any = (layer: string) => (dispatch: any) => {
  dispatch({
    type: 'SET_CLASSIFICATION_LAYER',
    payload: layer,
  });
};

export const putTrainingRegions: any = (journey, data) => () => {
  return _putTrainingRegions(journey.id, data);
};

const _putTrainingRegions = async (journeyId: string, trainingRegions: Feature[]) => {
  return API.put(`${FIELD_URL}/field-service/classification/training/${journeyId}`, trainingRegions)
    .then(() => {
      showToast('Success', 'Training layers saved successfully', 'success', 'training-region-saved');
    })
    .catch(() => {
      showToast('Error', 'Error saving training layers', 'error', 'training-region-error');
    });
};

export const downloadClassificationData: any = (journeyId: string, layers: any) => async () => {
  return triggerDownload(JSON.stringify(layers.classified), `classification_layer_${journeyId}`, 'geojson');
};

export const generateLayers: any = (journeyId: string, trainingRegions?: Feature[]) => async (dispatch: any) => {
  dispatch({
    type: 'GET_CLASSIFICATION_LAYERS_LOADING',
  });

  if (trainingRegions && trainingRegions.length > 0) {
    await _putTrainingRegions(journeyId, trainingRegions);
    dispatch(getTrainingRegions(journeyId));
  }

  return API.post(`${FIELD_URL}/field-service/classification/generate/${journeyId}`, {})
    .then(async () => {
      dispatch({
        type: 'GET_CLASSIFICATION_LAYERS_GENERATING',
      });
      showToast('Success', 'Classification layers generation started, it will be done in a few minutes', 'success', 'classification-layers-started');

      await waitExecution(journeyId);
      dispatch(getClassificationLayers(journeyId, true));
    })
    .catch((e) => {
      dispatch({
        type: 'GET_CLASSIFICATION_LAYERS_ERRORED',
        payload: e,
      });
    });
};

const waitExecution = async (journeyId: string) => {
  return new Promise((resolve) => {
    const id = setInterval(async () => {
      const result = await isGeneratingLayers(journeyId);
      if (result.status === 'finished') {
        clearInterval(id);
        resolve(true);
      }
    }, 10000);
  });
};

const isGeneratingLayers = async (journeyId: string) => {
  const response = await API.get(`${FIELD_URL}/field-service/classification/check-queue/${journeyId}`, {});
  return response.data;
};

export const removeTreesFromField: any = (journeyId: string, field: Feature) => (dispatch: any) => {
  dispatch({
    type: 'REMOVE_FROM_FIELD_REQUESTED',
    payload: { id: field.id },
  });
  API.post(`${FIELD_URL}/field-service/classification/remove/${journeyId}`, { field }).then((response) => {
    console.log(response);
    dispatch({
      type: 'REMOVE_FROM_FIELD_SUCCEEDED',
      payload: { id: field.id, data: response.data },
    });
  });
};

export const resetField: any = (fieldId: string) => (dispatch: any) => {
  dispatch({
    type: 'RESET_FIELD',
    payload: { id: fieldId },
  });
};

export const getClassificationLayers: any =
  (journeyId: string, show = false) =>
  async (dispatch: any) => {
    dispatch({
      type: 'GET_CLASSIFICATION_LAYERS_REQUESTED',
    });

    const isGenerating = await isGeneratingLayers(journeyId);

    if (isGenerating.status === 'queued') {
      dispatch({
        type: 'GET_CLASSIFICATION_LAYERS_GENERATING',
      });
      await waitExecution(journeyId);
    }

    API.get(`${FIELD_URL}/field-service/classification/layers/${journeyId}`)
      .then(async ({ data }) => {
        if (Object.values(data).every((a) => !a)) {
          dispatch({
            type: 'GET_CLASSIFICATION_LAYERS_UNAVAILABLE',
          });
          return;
        }
        dispatch({
          type: 'GET_CLASSIFICATION_LAYERS_LOADING',
        });
        const result = {
          classification_data: await API.downloadJsonSignedUrl(data.classification_data, 'classification_data'),
          layers: {},
          processed: [],
        };
        for (const layer of Object.keys(data)) {
          if (layer.includes('data')) continue;
          if (data[layer]) {
            result.layers[layer] = await API.downloadJsonSignedUrl(data[layer], layer);
          } else {
            result.layers[layer] = null;
          }
        }
        // const newFields: { data: any } = await API.get(`${FIELD_URL}/field-service/classification/processed/${journeyId}`);
        // if (newFields.data && newFields.data.length) {
        //   result.processed = newFields.data;
        // }
        dispatch({
          type: 'GET_CLASSIFICATION_LAYERS_SUCCEEDED',
          payload: result,
        });
        if (show) {
          dispatch(setClassificationLayer({ value: result.layers['classified'] ? 'classified' : 'classified_dw' }));
        }
      })
      .catch((e) => {
        dispatch({
          type: 'GET_CLASSIFICATION_LAYERS_ERRORED',
          payload: e,
        });
      });
  };

// export const subtractClassificationLayer: any = (fields: FeatureCollection, layer: FeatureCollection) => (dispatch: any) => {
//   const result = turf.difference(turf.multiPolygon(fields), layer);
//   dispatch({
//     type: 'SUBTRACT_CLASSIFICATION_LAYER',
//     payload: result,
//   });
// }
